import { supabase } from './supabase';
import { compressImageBothSizes } from './utils/imageCompression';

// ==================== UPLOAD MULTIPLE PHOTOS (WITH MODERATION) ====================
// captions: array of caption strings (one per file), can be null/undefined for no captions
// Each caption is limited to 100 characters (validation should happen in UI)
export const uploadPhotos = async (userId, files, captions = null, onStatusUpdate = null) => {
  const uploads = files.map(async (file, index) => {
    // Get caption for this specific image (if captions array provided)
    const caption = captions && Array.isArray(captions) && captions[index] 
      ? captions[index].trim().slice(0, 100) // Trim and enforce 100 char limit
      : null;
    // Compress images to both sizes first
    let feedFile, fullFile;
    try {
      const compressed = await compressImageBothSizes(file);
      feedFile = compressed.feedFile;
      fullFile = compressed.fullFile;
    } catch (compressionError) {
      console.error('Compression error:', compressionError);
      // If compression fails, use original file for both
      feedFile = file;
      fullFile = file;
    }

    // Try Edge Function with moderation first (using full-size image)
    try {
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }


      // Create form data with full-size image for moderation
      const formData = new FormData();
      formData.append('file', fullFile);
      formData.append('userId', userId);
      // Include caption if provided (Edge Function may handle it, or we'll update it after)
      if (caption) {
        formData.append('caption', caption);
      }

      // Call Edge Function using fetch (not invoke) to properly send multipart/form-data
      const functionUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/moderate-upload`;
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          // Don't set Content-Type - browser will set it with boundary for multipart/form-data
        },
        body: formData,
      });

      // Add this logging
      console.log('Edge Function Response Status:', response.status);

      // Parse response
      let data = {};
      try {
        const responseText = await response.text();
        console.log('Edge Function Response Text:', responseText); // Add this
        if (responseText) {
          data = JSON.parse(responseText);
          console.log('Edge Function Response Data:', data); // Add this
        }
      } catch (parseError) {
        console.error('Failed to parse Edge Function response:', parseError);
        // If we can't parse, it's likely the function doesn't exist or returned HTML/error page
        throw new Error('EDGE_FUNCTION_ERROR');
      }

      if (!response.ok || !data.ok) {
        // Log the actual error for debugging
        console.error('Edge Function error:', {
          status: response.status,
          statusText: response.statusText,
          error: data.error,
          message: data.message,
          fullResponse: data
        });

        if (data.error === 'NSFW_BLOCKED') {
          throw new Error('This image contains inappropriate content and cannot be uploaded');
        }
        // For other errors, fall back to direct upload
        throw new Error('EDGE_FUNCTION_ERROR');
      }

      // Edge Function uploaded full-size image and returned photo data
      const photoData = data?.data;
      const fullUrl = photoData?.url;

      if (!fullUrl) {
        throw new Error('Edge Function did not return image URL');
      }

      // Now upload feed-size version
      const fileExt = 'jpg'; // Always use jpg for compressed images
      const feedFileName = `${userId}/${Date.now()}-${Math.random().toString(36).substring(7)}-feed.${fileExt}`;
      
      const { data: feedUploadData, error: feedUploadError } = await supabase.storage
        .from('photos')
        .upload(feedFileName, feedFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (feedUploadError) {
        console.error('Feed image upload error:', feedUploadError);
        // If feed upload fails, continue with full-size only (feed_url will be null)
        // But we still need to update caption if provided
        if (caption !== null) {
          const { error: captionUpdateError } = await supabase
            .from('photos')
            .update({ caption: caption })
            .eq('id', photoData.id);
          
          if (captionUpdateError) {
            console.error('Failed to update caption:', captionUpdateError);
          } else {
            return { ...photoData, caption: caption };
          }
        }
      } else {
        // Get feed URL and update database
        const { data: { publicUrl: feedUrl } } = supabase.storage
          .from('photos')
          .getPublicUrl(feedFileName);

        // Update the photo record with feed_url and caption
        const updateData = { feed_url: feedUrl };
        if (caption !== null) {
          updateData.caption = caption;
        }
        
        const { error: updateError } = await supabase
          .from('photos')
          .update(updateData)
          .eq('id', photoData.id);

        if (updateError) {
          console.error('Failed to update feed_url/caption:', updateError);
          // Try to clean up feed image if DB update fails
          if (feedUploadData?.path) {
            await supabase.storage.from('photos').remove([feedUploadData.path]);
          }
        } else {
          // Return updated photo data with feed_url and caption
          return { ...photoData, feed_url: feedUrl, caption: caption };
        }
      }

      return photoData;
    } catch (edgeFunctionError) {
      // If it's an NSFW error, throw it immediately (don't fall back)
      if (edgeFunctionError.message && edgeFunctionError.message.includes('inappropriate content')) {
        throw edgeFunctionError;
      }

      // If it's any other error (not NSFW), fall back to direct upload
      if (edgeFunctionError.message && edgeFunctionError.message.includes('EDGE_FUNCTION_ERROR')) {
        console.warn('Using direct upload (moderation not available):', edgeFunctionError);
      } else {
        // Unexpected error - log it but still fall back
        console.warn('Unexpected error from Edge Function, falling back to direct upload:', edgeFunctionError);
      }

      // Direct upload fallback - upload both sizes
      const fileExt = 'jpg'; // Always use jpg for compressed images
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(7);
      const fullFileName = `${userId}/${timestamp}-${randomStr}-full.${fileExt}`;
      const feedFileName = `${userId}/${timestamp}-${randomStr}-feed.${fileExt}`;
      
      // Upload both sizes
      const [fullUploadResult, feedUploadResult] = await Promise.all([
        supabase.storage
          .from('photos')
          .upload(fullFileName, fullFile, {
            cacheControl: '3600',
            upsert: false
          }),
        supabase.storage
          .from('photos')
          .upload(feedFileName, feedFile, {
            cacheControl: '3600',
            upsert: false
          }),
      ]);
      
      if (fullUploadResult.error) {
        console.error('Full image upload error:', fullUploadResult.error);
        throw new Error(`Upload failed: ${fullUploadResult.error.message || 'Unknown error'}`);
      }

      // Get public URLs
      const { data: { publicUrl: fullUrl } } = supabase.storage
        .from('photos')
        .getPublicUrl(fullFileName);

      let feedUrl = null;
      if (!feedUploadResult.error) {
        const { data: { publicUrl } } = supabase.storage
          .from('photos')
          .getPublicUrl(feedFileName);
        feedUrl = publicUrl;
      } else {
        console.error('Feed image upload error (continuing with full-size only):', feedUploadResult.error);
      }

      // Save to database with both URLs and caption
      const { data, error } = await supabase
        .from('photos')
        .insert({ 
          user_id: userId, 
          url: fullUrl,
          feed_url: feedUrl, // Will be null if feed upload failed
          caption: caption // Will be null if no caption provided
        })
        .select()
        .single();

      if (error) {
        console.error('Database insert error:', error);
        // Try to clean up uploaded files if database insert fails
        const pathsToClean = [fullUploadResult.data?.path];
        if (feedUploadResult.data?.path) {
          pathsToClean.push(feedUploadResult.data.path);
        }
        if (pathsToClean.length > 0) {
          await supabase.storage.from('photos').remove(pathsToClean);
        }
        throw new Error(`Database error: ${error.message || 'Unknown error'}`);
      }

      return data;
    }
  });

  return await Promise.all(uploads);
};

// ==================== GET USER'S PHOTOS ====================
export const getUserPhotos = async (userId, currentUserId = null) => {
  // If a currentUserId is provided, check if there's a block relationship
  if (currentUserId && currentUserId !== userId) {
    const { data: blockData, error: blockError } = await supabase
      .from('blocked_users')
      .select('id')
      .or(`and(blocker_id.eq.${userId},blocked_id.eq.${currentUserId}),and(blocker_id.eq.${currentUserId},blocked_id.eq.${userId})`)
      .maybeSingle();
    
    if (blockError) throw blockError;
    
    // If there's any block relationship (either direction), return empty array
    if (blockData) {
      return [];
    }
  }
  
  const { data, error } = await supabase
    .from('photos')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
};

// ==================== DELETE PHOTO ====================
export const deletePhoto = async (photoId, photoUrl) => {
  // Delete from database
  const { error: dbError } = await supabase
    .from('photos')
    .delete()
    .eq('id', photoId);
  
  if (dbError) throw dbError;
  
  // Delete from storage
  const path = photoUrl.split('/photos/')[1];
  const { error: storageError } = await supabase.storage
    .from('photos')
    .remove([path]);
  
  if (storageError) console.error('Storage delete error:', storageError);
  
  return true;
};

// ==================== SEARCH USERS ====================
export const searchUsers = async (searchTerm, currentUserId = null) => {
  // First, get list of blocked user IDs (both directions) if user is logged in
  let blockedUserIds = [];
  if (currentUserId) {
    const { data: blockedData, error: blockedError } = await supabase
      .from('blocked_users')
      .select('blocker_id, blocked_id')
      .or(`blocker_id.eq.${currentUserId},blocked_id.eq.${currentUserId}`);
    
    if (!blockedError && blockedData && blockedData.length > 0) {
      // Extract all blocked user IDs (bidirectional)
      blockedData.forEach(block => {
        if (block.blocker_id === currentUserId) {
          blockedUserIds.push(block.blocked_id);
        } else {
          blockedUserIds.push(block.blocker_id);
        }
      });
    }
  }
  
  // Build query with blocked users excluded at database level
  let query = supabase
    .from('profiles')
    .select('id, username, name, avatar, bio')
    .ilike('username', `%${searchTerm}%`)
    .limit(20);
  
  // Exclude the logged-in user's own profile from search results
  if (currentUserId) {
    query = query.neq('id', currentUserId);
  }
  
  // Exclude blocked users at DATABASE level using NOT IN
  if (blockedUserIds.length > 0) {
    if (blockedUserIds.length === 1) {
      // Use .neq() for single blocked user
      query = query.neq('id', blockedUserIds[0]);
    } else {
      // Use .not('in', array) for multiple blocked users
      query = query.not('id', 'in', `(${blockedUserIds.join(',')})`);
    }
  }
  
  const { data, error } = await query;
  
  if (error) throw error;
  
  return data || [];
};

// ==================== GET USER BY EXACT USERNAME (FAST PATH FOR URL ROUTES) ====================
// Optimized for direct username routes - uses exact match instead of pattern search
// This is MUCH faster than searchUsers because it can use database indexes
export const getUserByExactUsername = async (username, currentUserId = null) => {
  // Use exact match (case-insensitive) - no wildcards, so database can use indexes
  let query = supabase
    .from('profiles')
    .select('id, username, name, avatar, bio')
    .ilike('username', username) // Exact match - no % wildcards
    .limit(1);
  
  // Exclude the logged-in user's own profile from results
  if (currentUserId) {
    query = query.neq('id', currentUserId);
  }
  
  const { data, error } = await query;
  
  if (error) throw error;
  
  return data && data.length > 0 ? data[0] : null;
};

// ==================== GET USER PROFILE ====================
export const getUserProfile = async (userId, currentUserId = null) => {
  // If a currentUserId is provided, check if there's a block relationship
  if (currentUserId && currentUserId !== userId) {
    const { data: blockData, error: blockError } = await supabase
      .from('blocked_users')
      .select('id')
      .or(`and(blocker_id.eq.${userId},blocked_id.eq.${currentUserId}),and(blocker_id.eq.${currentUserId},blocked_id.eq.${userId})`)
      .maybeSingle();
    
    if (blockError) throw blockError;
    
    // If there's any block relationship (either direction), throw error
    if (blockData) {
      throw new Error('This profile is unavailable');
    }
  }
  
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  
  if (error) throw error;
  return data;
};

// ==================== UPDATE PROFILE PIC ====================
export const uploadProfilePic = async (userId, file) => {
  // Get file extension from original file
  const fileExt = file.name.split('.').pop() || 'jpg';
  const fileName = `${userId}/profile.${fileExt}`;
  
  // Upload (upsert = overwrite if exists)
  const { error: uploadError } = await supabase.storage
    .from('profile-pics')
    .upload(fileName, file, { 
      upsert: true,
      cacheControl: '3600'
    });
  
  if (uploadError) {
    console.error('Profile pic upload error:', uploadError);
    throw new Error(`Upload failed: ${uploadError.message || 'Unknown error'}`);
  }

  // Get public URL
  const { data: { publicUrl } } = supabase.storage
    .from('profile-pics')
    .getPublicUrl(fileName);

  // Update profile in database
  const { error: updateError } = await supabase
    .from('profiles')
    .update({ avatar: publicUrl })
    .eq('id', userId);
  
  if (updateError) {
    console.error('Profile update error:', updateError);
    throw new Error(`Database error: ${updateError.message || 'Unknown error'}`);
  }

  return publicUrl;
};

// ==================== UPDATE BIO ====================
export const updateBio = async (userId, bio) => {
  const { error } = await supabase
    .from('profiles')
    .update({ bio })
    .eq('id', userId);
  
  if (error) throw error;
  return true;
};

// ==================== GET USERS I FOLLOW ====================
export const getFollowing = async (userId) => {
  // First, get list of blocked user IDs (both directions)
  const { data: blockedData, error: blockedError } = await supabase
    .from('blocked_users')
    .select('blocker_id, blocked_id')
    .or(`blocker_id.eq.${userId},blocked_id.eq.${userId}`);
  
  if (blockedError) throw blockedError;
  
  // Extract all blocked user IDs (bidirectional)
  const blockedUserIds = [];
  (blockedData || []).forEach(block => {
    if (block.blocker_id === userId) {
      blockedUserIds.push(block.blocked_id);
    } else {
      blockedUserIds.push(block.blocker_id);
    }
  });
  
  // Build query to get following, excluding blocked users at DB level
  let followsQuery = supabase
    .from('follows')
    .select(`
      following_id,
      profiles!follows_following_id_fkey(
        id,
        username,
        name,
        avatar,
        bio
      )
    `)
    .eq('follower_id', userId);
  
  // Exclude blocked users at DATABASE level
  if (blockedUserIds.length > 0) {
    if (blockedUserIds.length === 1) {
      // Use .neq() for single blocked user
      followsQuery = followsQuery.neq('following_id', blockedUserIds[0]);
    } else {
      // Use .not('in', array) for multiple blocked users
      followsQuery = followsQuery.not('following_id', 'in', `(${blockedUserIds.join(',')})`);
    }
  }
  
  const { data, error } = await followsQuery;
  
  if (error) throw error;
  
  // Extract the profiles from the nested structure
  const following = (data || []).map(item => item.profiles).filter(p => p); // Filter out null profiles
  
  return following;
};

// ==================== GET MY FOLLOWERS ====================
export const getFollowers = async (userId) => {
  console.log('getFollowers - Starting with userId:', userId);
  
  // First, get list of blocked user IDs (both directions)
  const { data: blockedData, error: blockedError } = await supabase
    .from('blocked_users')
    .select('blocker_id, blocked_id')
    .or(`blocker_id.eq.${userId},blocked_id.eq.${userId}`);
  
  if (blockedError) {
    console.error('getFollowers - Blocked users query error:', blockedError);
    throw blockedError;
  }
  
  console.log('getFollowers - Blocked users data:', blockedData);
  
  // Extract all blocked user IDs (bidirectional)
  const blockedUserIds = [];
  (blockedData || []).forEach(block => {
    if (block.blocker_id === userId) {
      blockedUserIds.push(block.blocked_id);
    } else {
      blockedUserIds.push(block.blocker_id);
    }
  });
  
  console.log('getFollowers - Blocked user IDs:', blockedUserIds);
  
  // Build query to get followers, excluding blocked users at DB level
  // First get the follower_ids
  let followsQuery = supabase
    .from('follows')
    .select('follower_id')
    .eq('following_id', userId);
  
  console.log('getFollowers - Base follows query for following_id:', userId);
  
  // Get all followers first (without blocking filter to avoid syntax issues)
  const { data: followsDataAll, error: followsErrorAll } = await followsQuery;
  
  if (followsErrorAll) {
    console.error('getFollowers - Follows query error:', followsErrorAll);
    throw followsErrorAll;
  }
  
  console.log('getFollowers - Follows data BEFORE blocking filter:', followsDataAll);
  console.log('getFollowers - Number of follows BEFORE blocking filter:', followsDataAll?.length || 0);
  
  // If no followers, return empty array
  if (!followsDataAll || followsDataAll.length === 0) {
    console.log('getFollowers - No followers found, returning empty array');
    return [];
  }
  
  // Filter out blocked users in JavaScript (more reliable than SQL filter)
  let followsData = followsDataAll;
  if (blockedUserIds.length > 0) {
    console.log('getFollowers - Filtering out blocked users in JavaScript:', blockedUserIds);
    followsData = followsDataAll.filter(f => !blockedUserIds.includes(f.follower_id));
    console.log('getFollowers - Follows data AFTER JavaScript filtering:', followsData);
    console.log('getFollowers - Number of follows AFTER filtering:', followsData?.length || 0);
  }
  
  // Check if we have any follows after filtering
  if (!followsData || followsData.length === 0) {
    console.log('getFollowers - No followers found after filtering, returning empty array');
    return [];
  }
  
  console.log('getFollowers - Final follows data:', followsData);
  console.log('getFollowers - Final number of follows:', followsData.length);
  
  // Extract follower IDs
  const followerIds = followsData.map(f => f.follower_id);
  console.log('getFollowers - Follower IDs extracted:', followerIds);
  
  // Now get the profiles for these follower IDs
  const { data: profilesData, error: profilesError } = await supabase
    .from('profiles')
    .select('id, username, name, avatar, bio')
    .in('id', followerIds);
  
  if (profilesError) {
    console.error('getFollowers - Profiles query error:', profilesError);
    throw profilesError;
  }
  
  console.log('getFollowers - Profiles data:', profilesData);
  console.log('getFollowers - Number of profiles:', profilesData?.length || 0);
  console.log('getFollowers - Returning profiles:', profilesData || []);
  
  return profilesData || [];
};

// ==================== FOLLOW USER ====================
export const followUser = async (followerId, followingId) => {
  const { error } = await supabase
    .from('follows')
    .insert({
      follower_id: followerId,
      following_id: followingId
    });
  
  if (error) throw error;
  return true;
};

// ==================== UNFOLLOW USER ====================
export const unfollowUser = async (followerId, followingId) => {
  const { error } = await supabase
    .from('follows')
    .delete()
    .eq('follower_id', followerId)
    .eq('following_id', followingId);
  
  if (error) throw error;
  return true;
};

// ==================== CHECK IF FOLLOWING ====================
export const checkIsFollowing = async (followerId, followingId) => {
  const { data, error } = await supabase
    .from('follows')
    .select('id')
    .eq('follower_id', followerId)
    .eq('following_id', followingId)
    .maybeSingle();
  
  if (error) throw error;
  return !!data;
};

// ==================== GET HOME FEED ====================
// Get recent posts from users that the current user follows
export const getHomeFeed = async (userId, limit = 20, offset = 0) => {
  // First, get list of blocked user IDs (both directions) - do this BEFORE getting follows
  const { data: blockedData, error: blockedError } = await supabase
    .from('blocked_users')
    .select('blocker_id, blocked_id')
    .or(`blocker_id.eq.${userId},blocked_id.eq.${userId}`);
  
  if (blockedError) throw blockedError;
  
  // Extract all blocked user IDs (bidirectional)
  const blockedUserIds = [];
  (blockedData || []).forEach(block => {
    if (block.blocker_id === userId) {
      blockedUserIds.push(block.blocked_id);
    } else {
      blockedUserIds.push(block.blocker_id);
    }
  });
  
  // Get the list of user IDs that the current user follows, EXCLUDING blocked users at DB level
  let followsQuery = supabase
    .from('follows')
    .select('following_id')
    .eq('follower_id', userId);
  
  // Exclude blocked users from follows query at DATABASE level
  if (blockedUserIds.length > 0) {
    if (blockedUserIds.length === 1) {
      // Use .neq() for single blocked user
      followsQuery = followsQuery.neq('following_id', blockedUserIds[0]);
    } else {
      // Use .not('in', array) for multiple blocked users
      followsQuery = followsQuery.not('following_id', 'in', `(${blockedUserIds.join(',')})`);
    }
  }
  
  const { data: followsData, error: followsError } = await followsQuery;
  
  if (followsError) throw followsError;
  
  // If not following anyone (or everyone is blocked), return empty array
  if (!followsData || followsData.length === 0) {
    return [];
  }
  
  // Extract the valid following IDs (already filtered by DB)
  const validFollowingIds = followsData.map(f => f.following_id);
  
  // Now get photos from those users with profile info
  const { data, error } = await supabase
    .from('photos')
    .select(`
      *,
      profiles!photos_user_id_fkey(
        id,
        username,
        name,
        avatar
      )
    `)
    .in('user_id', validFollowingIds)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);
  
  if (error) throw error;
  
  // Flatten the structure to make it easier to work with
  return (data || []).map(photo => ({
    ...photo,
    username: photo.profiles?.username,
    user_name: photo.profiles?.name,
    user_avatar: photo.profiles?.avatar,
    user_id: photo.profiles?.id
  }));
};



// ==================== BLOCK USER ====================
export const blockUser = async (blockerId, blockedId) => {
  // CRITICAL: Remove ALL follow relationships between these two users in BOTH directions
  // This is a simple DELETE that removes any follows between them
  const { error: unfollowError } = await supabase
    .from('follows')
    .delete()
    .or(`and(follower_id.eq.${blockerId},following_id.eq.${blockedId}),and(follower_id.eq.${blockedId},following_id.eq.${blockerId})`);
  
  if (unfollowError) {
    console.error('Error removing follow relationships:', unfollowError);
    // Don't throw - continue with block even if unfollow fails
  }
  
  // Now add the block
  const { error } = await supabase
    .from('blocked_users')
    .insert({
      blocker_id: blockerId,
      blocked_id: blockedId
    });
  
  if (error) {
    // Handle duplicate block attempts gracefully
    if (error.code === '23505') { // Unique constraint violation
      return true; // Already blocked, that's fine
    }
    throw error;
  }
  return true;
};

// ==================== UNBLOCK USER ====================
export const unblockUser = async (blockerId, blockedId) => {
  const { error } = await supabase
    .from('blocked_users')
    .delete()
    .eq('blocker_id', blockerId)
    .eq('blocked_id', blockedId);
  
  if (error) throw error;
  return true;
};

// ==================== CHECK IF BLOCKED ====================
export const checkIsBlocked = async (userId, otherUserId) => {
  const { data, error } = await supabase
    .from('blocked_users')
    .select('id')
    .or(`and(blocker_id.eq.${userId},blocked_id.eq.${otherUserId}),and(blocker_id.eq.${otherUserId},blocked_id.eq.${userId})`)
    .maybeSingle();
  
  if (error) throw error;
  return !!data;
};

// ==================== GET BLOCKED USERS ====================
export const getBlockedUsers = async (userId) => {
  const { data, error } = await supabase
    .from('blocked_users')
    .select(`
      blocked_id,
      profiles!blocked_users_blocked_id_fkey(
        id,
        username,
        name,
        avatar
      )
    `)
    .eq('blocker_id', userId);
  
  if (error) throw error;
  return (data || []).map(item => item.profiles);
};

// ==================== GET BLOCKED USER IDS (CURRENT USER BLOCKED) ====================
export const getBlockedUserIds = async (userId) => {
  const { data, error } = await supabase
    .from('blocked_users')
    .select('blocked_id')
    .eq('blocker_id', userId);

  if (error) throw error;
  return (data || []).map(item => item.blocked_id);
};

// ==================== REPORT PHOTO OR USER ====================
// reportType: 'photo' or 'user'
// photoId: required for 'photo' reports, should be null for 'user' reports
export const reportPhoto = async (reportType, reporterId, reportedUserId, reason, photoId = null, additionalDetails = null) => {
  // Validate report type
  if (reportType !== 'photo' && reportType !== 'user') {
    throw new Error('Invalid report type. Must be "photo" or "user"');
  }
  
  // Validate that photoId is provided for photo reports
  if (reportType === 'photo' && !photoId) {
    throw new Error('Photo ID is required for photo reports');
  }
  
  // Validate that photoId is null for user reports
  if (reportType === 'user' && photoId !== null) {
    throw new Error('Photo ID must be null for user reports');
  }
  
  // Build the insert object
  const insertData = {
    report_type: reportType,
    reporter_id: reporterId,
    reported_user_id: reportedUserId,
    reason: reason, // 'nudity', 'hate', 'spam', 'violence', 'other'
    additional_details: additionalDetails
  };
  
  // Only include photo_id for photo reports
  if (reportType === 'photo') {
    insertData.photo_id = photoId;
  } else {
    insertData.photo_id = null; // Explicitly set to null for user reports
  }
  
  const { data, error } = await supabase
    .from('reports')
    .insert(insertData)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// ==================== GET USER'S REPORTS ====================
export const getUserReports = async (userId) => {
  const { data, error } = await supabase
    .from('reports')
    .select(`
      *,
      photos(url, caption),
      profiles!reports_reported_user_id_fkey(username, name, avatar)
    `)
    .eq('reporter_id', userId)
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
};

// ==================== UPDATE HOME FEED (with blocking & status filtering) ====================
// Enhanced version that also filters by user/photo status
export const getHomeFeedWithBlocking = async (userId, limit = 20, offset = 0) => {
  // First, get list of blocked user IDs (both directions)
  const { data: blockedData, error: blockedError } = await supabase
    .from('blocked_users')
    .select('blocker_id, blocked_id')
    .or(`blocker_id.eq.${userId},blocked_id.eq.${userId}`);
  
  if (blockedError) throw blockedError;
  
  // Extract all blocked user IDs (bidirectional)
  const blockedUserIds = [];
  (blockedData || []).forEach(block => {
    if (block.blocker_id === userId) {
      blockedUserIds.push(block.blocked_id);
    } else {
      blockedUserIds.push(block.blocker_id);
    }
  });
  
  // Get list of following IDs, excluding blocked users at DB level
  let followsQuery = supabase
    .from('follows')
    .select('following_id')
    .eq('follower_id', userId);
  
  // Exclude blocked users at DATABASE level
  if (blockedUserIds.length > 0) {
    if (blockedUserIds.length === 1) {
      // Use .neq() for single blocked user
      followsQuery = followsQuery.neq('following_id', blockedUserIds[0]);
    } else {
      // Use .not('in', array) for multiple blocked users
      followsQuery = followsQuery.not('following_id', 'in', `(${blockedUserIds.join(',')})`);
    }
  }
  
  const { data: followsData, error: followsError } = await followsQuery;
  
  if (followsError) throw followsError;
  
  if (!followsData || followsData.length === 0) {
    return [];
  }
  
  const validFollowingIds = followsData.map(f => f.following_id);
  
  // Get photos from valid users, only active photos from active profiles
  const { data, error } = await supabase
    .from('photos')
    .select(`
      *,
      profiles!photos_user_id_fkey(
        id,
        username,
        name,
        avatar,
        status
      )
    `)
    .in('user_id', validFollowingIds)
    .eq('status', 'active') // Only show active photos
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);
  
  if (error) throw error;
  
  // Filter out photos from banned/suspended users and flatten structure
  return (data || [])
    .filter(photo => photo.profiles?.status === 'active')
    .map(photo => ({
      ...photo,
      username: photo.profiles?.username,
      user_name: photo.profiles?.name,
      user_avatar: photo.profiles?.avatar,
      user_id: photo.profiles?.id
    }));
};

// ==================== UPDATE GET USER PHOTOS (with status filter) ====================
// Replace your existing getUserPhotos with this version
export const getUserPhotosActive = async (userId) => {
  const { data, error } = await supabase
    .from('photos')
    .select('*')
    .eq('user_id', userId)
    .eq('status', 'active') // Only show active photos
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
};

// ==================== DELETE USER ACCOUNT ====================
// Permanently deletes all user data: photos, follows, blocked users, profile, and auth account
export const deleteAccount = async (userId) => {
  try {
    // 1. Get all user's photos to delete from storage
    const { data: photos, error: photosError } = await supabase
      .from('photos')
      .select('id, url')
      .eq('user_id', userId);
    
    if (photosError) throw photosError;
    
    // 2. Delete all photo files from storage
    if (photos && photos.length > 0) {
      const pathsToDelete = photos
        .map(photo => {
          const pathMatch = photo.url?.match(/\/photos\/(.+)$/);
          return pathMatch ? pathMatch[1] : null;
        })
        .filter(Boolean);
      
      if (pathsToDelete.length > 0) {
        const { error: storageError } = await supabase.storage
          .from('photos')
          .remove(pathsToDelete);
        
        if (storageError) {
          console.error('Storage delete error:', storageError);
          // Continue even if storage deletion fails
        }
      }
    }
    
    // 3. Delete all photos from database
    const { error: deletePhotosError } = await supabase
      .from('photos')
      .delete()
      .eq('user_id', userId);
    
    if (deletePhotosError) throw deletePhotosError;
    
    // 4. Delete all follow relationships (both directions)
    const { error: deleteFollowsError1 } = await supabase
      .from('follows')
      .delete()
      .eq('follower_id', userId);
    
    if (deleteFollowsError1) throw deleteFollowsError1;
    
    const { error: deleteFollowsError2 } = await supabase
      .from('follows')
      .delete()
      .eq('following_id', userId);
    
    if (deleteFollowsError2) throw deleteFollowsError2;
    
    // 5. Delete all blocked user relationships (both directions)
    const { error: deleteBlocksError1 } = await supabase
      .from('blocked_users')
      .delete()
      .eq('blocker_id', userId);
    
    if (deleteBlocksError1) throw deleteBlocksError1;
    
    const { error: deleteBlocksError2 } = await supabase
      .from('blocked_users')
      .delete()
      .eq('blocked_id', userId);
    
    if (deleteBlocksError2) throw deleteBlocksError2;
    
    // 6. Delete all reports made by the user
    const { error: deleteReportsError } = await supabase
      .from('reports')
      .delete()
      .eq('reporter_id', userId);
    
    if (deleteReportsError) throw deleteReportsError;
    
    // 7. Delete profile
    const { error: deleteProfileError } = await supabase
      .from('profiles')
      .delete()
      .eq('id', userId);
    
    if (deleteProfileError) throw deleteProfileError;
    
    // 8. Delete auth user (this will be handled by the auth context)
    // The auth context will call supabase.auth.admin.deleteUser() or similar
    // For now, we'll return success and let the auth context handle it
    
    return true;
  } catch (error) {
    console.error('Error deleting account:', error);
    throw error;
  }
};